<template>
  <div class="container py-5">
    <div class="row">
      <div class="col-md-10 mx-auto">
        <!-- Vérification de connexion -->
        <div v-if="!isAuthenticated" class="alert alert-info mb-4">
          <h5><i class="fas fa-info-circle me-2"></i>Connexion requise</h5>
          <p>Vous devez être connecté pour postuler à cette offre.</p>
          <div class="d-flex gap-2">
            <router-link to="/login" class="btn btn-primary">
              <i class="fas fa-sign-in-alt me-2"></i>Se connecter
            </router-link>
            <router-link to="/register" class="btn btn-outline-primary">
              <i class="fas fa-user-plus me-2"></i>S'inscrire
            </router-link>
          </div>
        </div>

        <!-- Message si profil incomplet -->
        <div v-else-if="!user.has_completed_profile" class="alert alert-warning mb-4">
          Veuillez remplir votre profil pour plus de chance
        </div>

        <!-- Détails de l'offre -->
        <div class="card mb-4">
          <div class="card-header bg-primary text-white">
            <h4 class="mb-0">{{ jobOffer.title }}</h4>
          </div>
          <div class="card-body">
            <div class="mb-4">
              <h5>Description du poste</h5>
                <!-- Show description as plain text, not raw HTML -->
              <p class="card-text">{{ truncateDescription(stripHtmlTags(jobOffer.description)) }}</p>
            </div>
            <div class="mb-4">
              <h5>Prérequis</h5>
                <p class="card-text">{{ truncateDescription(stripHtmlTags(jobOffer.requirements)) }}</p>
            </div>
            <div class="text-danger">
              <strong>Date limite :</strong> {{ formatDate(jobOffer.deadline) }}
            </div>
          </div>
        </div>

        <!-- Formulaire de candidature -->
        <div v-if="isAuthenticated" class="card">
          <div class="card-header">
            <h5 class="mb-0">Documents requis</h5>
          </div>
          <div class="card-body">
            <form @submit.prevent="submitApplication">
              <!-- CV -->
              <div class="mb-4">
                <label class="form-label">CV (PDF)</label>
                <input 
                  type="file" 
                  class="form-control" 
                  accept=".pdf"
                  @change="handleFileUpload($event, 'cv')"
                  :class="{ 'is-invalid': errors.cv }"
                >
                <div class="invalid-feedback">{{ errors.cv }}</div>
                <small class="text-muted">Format PDF uniquement, max 2MB</small>
              </div>

              <!-- Lettre de motivation -->
              <div class="mb-4">
                <label class="form-label">Lettre de motivation (PDF)</label>
                <input 
                  type="file" 
                  class="form-control" 
                  accept=".pdf"
                  @change="handleFileUpload($event, 'lettre_motivation')"
                  :class="{ 'is-invalid': errors.lettre_motivation }"
                >
                <div class="invalid-feedback">{{ errors.lettre_motivation }}</div>
                <small class="text-muted">Format PDF uniquement, max 2MB</small>
              </div>

              <!-- Diplômes -->
              <div class="mb-4">
                <label class="form-label">Diplômes (PDF)</label>
                <input 
                  type="file" 
                  class="form-control" 
                  accept=".pdf"
                  multiple
                  @change="handleFileUpload($event, 'diplomes')"
                  :class="{ 'is-invalid': errors.diplomes }"
                >
                <div class="invalid-feedback">{{ errors.diplomes }}</div>
                <small class="text-muted">Format PDF uniquement, max 2MB par fichier</small>
              </div>

              <!-- Certificats (optionnel) -->
              <div class="mb-4">
                <label class="form-label">Certificats/Attestations/autres pièces demandées en PDF</label>
                <input 
                  type="file" 
                  class="form-control" 
                  accept=".pdf"
                  multiple
                  @change="handleFileUpload($event, 'certificats')"
                  :class="{ 'is-invalid': errors.certificats }"
                >
                <div class="invalid-feedback">{{ errors.certificats }}</div>
                <small class="text-muted">Format PDF uniquement, max 2MB par fichier</small>
              </div>

              <!-- Prétention salariale -->
              <div class="mb-4">
                <label class="form-label">Prétention salariale (FCFA)</label>
                <input 
                  type="number" 
                  class="form-control" 
                  v-model.number="pretentionSalariale"
                  placeholder="Ex: 250000"
                  min="0"
                  step="1000"
                  :class="{ 'is-invalid': errors.pretention_salariale }"
                >
                <div class="invalid-feedback">{{ errors.pretention_salariale }}</div>
                <small class="text-muted">Optionnel - Indiquez votre prétention salariale mensuelle en FCFA</small>
              </div>

              <div class="d-flex justify-content-between">
                <router-link :to="{ name: 'Home' }" class="btn btn-secondary">
                  Annuler
                </router-link>
                <button type="submit" class="btn btn-primary" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                  Soumettre ma candidature
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'JobApplication',
  
  inject: ['ToastSuccess', 'ToastError'],
  
  data() {
    return {
      user: {},
      jobOffer: {},
      formData: new FormData(),
      errors: {},
      loading: false,
      pretentionSalariale: ''
    }
  },

  computed: {
    isAuthenticated() {
      return !!localStorage.getItem('token')
    }
  },

  async created() {
    await this.loadJobOffer()
    if (this.isAuthenticated) {
      await this.loadProfile()
    }
  },

  methods: {
    // This method removes all HTML tags to safely display description as text only
    stripHtmlTags(html) {
      if (!html) return ''
      const div = document.createElement('div')
      div.innerHTML = html
      return div.textContent || div.innerText || ''
    },
    truncateDescription(description) {
      if (!description) return ''
      return description.length > 100 
        ? description.substring(0, 100) + '...' 
        : description
    },
    async loadProfile() {
      try {
        const response = await this.$http.get('/profile')
        this.user = response.data.profile
      } catch (error) {
        console.error('Erreur lors du chargement du profil:', error)
      }
    },

    async loadJobOffer() {
      try {
        const response = await this.$http.get(`/jobs/${this.$route.params.id}`)
        this.jobOffer = response.data.job_offer
      } catch (error) {
        this.ToastError.fire({
          title: "Erreur lors du chargement de l'offre"
        })
      }
    },

    handleFileUpload(event, fieldName) {
      const files = event.target.files
      if (!files.length) return

      // Vérification de la taille du fichier (2MB max)
      const maxSize = 2 * 1024 * 1024 // 2MB en octets
      if (files[0].size > maxSize) {
        this.errors[fieldName] = 'Le fichier ne doit pas dépasser 2MB'
        this.ToastError.fire({
          title: `${fieldName}: Le fichier ne doit pas dépasser 2MB`
        })
        event.target.value = '' // Réinitialiser l'input
        return
      }

      if (fieldName === 'cv' || fieldName === 'lettre_motivation') {
        this.formData.set(fieldName, files[0])
      } else {
        // Pour les champs multiples, on supprime d'abord les anciens fichiers
        for (let i = 0; i < this.formData.getAll(fieldName).length; i++) {
          this.formData.delete(fieldName)
        }
        // Puis on ajoute les nouveaux
        Array.from(files).forEach(file => {
          this.formData.append(`${fieldName}[]`, file)
        })
      }
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    async submitApplication() {
      if (!this.isAuthenticated) {
        this.$router.push('/login')
        return
      }

      this.loading = true
      this.errors = {}

      // Vérifier si les fichiers requis sont présents
      if (!this.formData.has('cv')) {
        this.errors.cv = 'Le CV est requis'
      }
      if (!this.formData.has('lettre_motivation')) {
        this.errors.lettre_motivation = 'La lettre de motivation est requise'
      }
      if (!this.formData.has('diplomes[]')) {
        this.errors.diplomes = 'Au moins un diplôme est requis'
      }

      if (Object.keys(this.errors).length > 0) {
        this.loading = false
        this.ToastError.fire({
          title: "Veuillez fournir tous les documents requis"
        })
        return
      }

      // Réinitialiser le formData
      const newFormData = new FormData()
      
      // Copier les fichiers existants
      for (let [key, value] of this.formData.entries()) {
        newFormData.append(key, value)
      }

      // Ajouter l'ID du poste
      newFormData.append('poste', this.$route.params.id)

      // Ajouter la prétention salariale si renseignée
      if (this.pretentionSalariale) {
        newFormData.append('pretention_salariale', this.pretentionSalariale)
      }

      try {
        await this.$http.post(`/jobs/${this.$route.params.id}/apply`, newFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        this.ToastSuccess.fire({
          title: "Candidature soumise avec succès"
        })
        this.$router.push('/status')
      } catch (error) {
        if (error.response?.status === 422) {
          this.errors = error.response.data.errors
          this.ToastError.fire({
            title: "Veuillez corriger les erreurs dans le formulaire"
          })
        } else {
          this.ToastError.fire({
            title: error.response?.data?.message || "Erreur lors de la soumission de la candidature"
          })
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script> 